import React, { FC, useCallback, useMemo } from 'react';
import styles from '../index.module.scss';
import { Skeleton } from 'src/common';
import classNames from 'classnames';
import BoardItem from '../../BoardItem';
import { OnVisibilityTrigger, useFetchMore } from 'src/common/infinityScroll';
import { useStagesBids } from 'src/models/bids/Bids/hooks';
import { useBoolean } from 'react-use';

interface IProps {
  stageId: string;
  isOver: boolean;
  isSameKanbanStatusOnDrop: boolean;
  container: Element | null;
}

export const BoardColumnContent: FC<IProps> = props => {
  const { stageId, isOver, isSameKanbanStatusOnDrop, container } = props;

  const [fetchMoreLoading, setFetchMoreLoading] = useBoolean(false);

  const { data: bidsData, loading, fetchMore } = useStagesBids({ stageId, amount: 10 });

  const stageItems = useMemo(() => {
    return bidsData?.bids || [];
  }, [bidsData]);

  const total = useMemo(() => bidsData?.total || 0, [bidsData]);
  const isMore = useMemo(() => {
    return bidsData && bidsData.bids.length < total;
  }, [bidsData, total]);

  const loadMore = useCallback(
    (variables: { offset: number }) => {
      setFetchMoreLoading(true);
      return fetchMore({
        variables
      }).finally(() => setFetchMoreLoading(false));
    },
    [fetchMore, setFetchMoreLoading]
  );

  const onLoadMore = useFetchMore({ offset: (bidsData && bidsData.bids.length) || 0 }, loadMore);
  return (
    <div className={styles.content}>
      {loading && <Skeleton loading active />}
      {!loading && (
        <ul className={styles.items}>
          <li className={classNames(styles.hoveredItem, { [styles.isOver]: isOver && !isSameKanbanStatusOnDrop })} />
          {stageItems.map((bid, idx) => (
            <BoardItem bid={bid} idx={idx} key={bid.id} />
          ))}
        </ul>
      )}
      {isMore && (
        <OnVisibilityTrigger
          isLoading={fetchMoreLoading}
          onVisible={onLoadMore}
          container={container}
          style={{ display: 'block' }}
        >
          <Skeleton active loading className={styles.skeleton} paragraph />
        </OnVisibilityTrigger>
      )}
    </div>
  );
};

export default BoardColumnContent;
